/**
 * THE GOVERNING-BODY MAP — pure. No React, no JSON import, no API, no I/O.
 *
 * `sr_conference` (label "SR Conference / Governing Body") is the only field that
 * names a governing body on a championship record, and it is NOT NORMALIZED: the
 * same association appears under an acronym AND a full name as two separate
 * values. 666 distinct strings stand for roughly 560 real bodies.
 *
 * ⚠ WHY THIS MODULE EXISTS, AND WHAT IT COSTS TO GET WRONG.
 *
 * "University Interscholastic League" (176 records) and "UIL" (37) are ONE
 * association in ONE state. A rule keyed on the raw string assigns the 176 and
 * leaves 37 Texas championships unowned **while reporting 100% coverage**. Texas
 * carries SR's largest official-marks expenditure, so those 37 are the records
 * where an unnoticed gap costs the most, and a false green is exactly how a gap
 * goes unnoticed.
 *
 * ⚠ AND WHY THE OBVIOUS RULE IS WRONG. The first version keyed merges on shared
 * initials plus (state, segment). That collapsed:
 *
 *     South Atlantic + Southwestern + Sooner  ->  one "SAC"
 *     Middle Atlantic + Mid-American          ->  one "MAC"
 *     Big South + Big Sky                     ->  one "BSC"
 *     Atlantic East + America East, Atlantic Sun + American Southwest,
 *     Great Northeast + Great Northwest
 *
 * A college conference carries NO state, so the discriminator was blank for
 * exactly the bodies that needed it, and six pairs of distinct conferences became
 * six single bodies. Nothing looked wrong: each merged body had a plausible
 * record count.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE RULE THIS FILE ENFORCES
 *
 *   1. A merge needs exactly ONE full name in its partition. Two distinct full
 *      names sharing initials are TWO BODIES, always, and the group is refused
 *      rather than merged.
 *   2. An acronym merges into a full name only when its state is UNANIMOUS
 *      across its records, never merely modal. Bare "IHSAA" at 20 Idaho + 9
 *      Indiana would move nine Indiana championships to the wrong rep.
 *   3. A blank state contradicts nothing, so it does not block a merge.
 *   4. It FAILS CLOSED. An acronym that cannot be resolved returns null and names
 *      every candidate it considered, the same discipline as `proposeScoped` in
 *      tools/bodies-scoped.mjs. Filling an unguardable record is how Michigan
 *      became Manitoba and how 419 American championships were queued to move to
 *      Canada.
 *
 * So HS state associations merge automatically (their state discriminates) and
 * college conferences are CURATED by hand below. That asymmetry is the finding,
 * not a shortcut.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * ⚠ THIS IS A READ-TIME DERIVATION AND WRITES NOTHING. `sr_conference` is a
 * PRIVILEGED field on `POST /api/champion-update` (exec or the `cc_data_repair`
 * grant), so normalizing it in HubSpot is a separate, gated act. Resolving at
 * read time is also the better design: one rule per body covers every
 * championship under it, and a championship created tomorrow inherits its body
 * with no write at all.
 */

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())

/** Strip a parenthetical qualifier: "MHSAA (Michigan)" -> "MHSAA". */
export const bareOf = (s) => S(s).replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim()

const STOPWORDS = new Set(['of', 'the', 'and', 'for', 'de', 'des'])

/** Initials of a multi-word name, dropping the words an acronym drops. */
export function initialsOf(name) {
  return S(name).replace(/\([^)]*\)/g, ' ')
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w && !STOPWORDS.has(w.toLowerCase()))
    .map((w) => w[0].toUpperCase())
    .join('')
}

/**
 * Is this string an ACRONYM form rather than a full name?
 *
 * Deliberately conservative: all-caps, at most 8 letters once punctuation and any
 * parenthetical are removed. "MHSAA (Michigan)" is an acronym form; "Mid-American
 * Conference" is not. Getting this wrong in the permissive direction is what lets
 * two full names into one partition, so the bar is high.
 */
export function looksLikeAcronym(s) {
  const b = bareOf(s)
  if (!b) return false
  return /^[A-Z0-9][A-Z0-9.\-/ ]*$/.test(b) && b.replace(/[^A-Za-z]/g, '').length <= 8
}

/* ---- the collisions, named ------------------------------------------------ */

/**
 * ACRONYMS THAT MEAN DIFFERENT BODIES. Listed explicitly rather than inferred,
 * because an inferred collision list is only as good as the data it was inferred
 * from, and this one has to hold for records that do not exist yet.
 *
 * Each entry is the acronym and the axis that separates its meanings.
 */
export const KNOWN_COLLISIONS = {
  MHSAA: { by: 'state', bodies: ['Mississippi', 'Michigan', 'Manitoba'] },
  IHSAA: { by: 'state', bodies: ['Idaho', 'Indiana', 'Iowa'] },
  WIAA: { by: 'state', bodies: ['Washington', 'Wisconsin'] },
  MIAA: { by: 'state+division', bodies: ['Massachusetts HS', 'Maryland HS', 'Mid-America D2', 'Michigan Intercollegiate D3'] },
  SAC: { by: 'division', bodies: ['South Atlantic D2', 'Sooner NAIA', 'Southwestern Athletic D1'] },
  AAC: { by: 'division', bodies: ['American Conference D1', 'Appalachian Athletic NAIA'] },
  CAA: { by: 'state+division', bodies: ['Canyon Athletic Association AZ HS', 'Coastal Athletic Association college'] },
  ACAA: { by: 'state', bodies: ['Alabama Christian', 'Atlantic Collegiate Canada'] },
  MAC: { by: 'division', bodies: ['Mid-American D1', 'Middle Atlantic D3', 'Musical Arts'] },
  GNAC: { by: 'region', bodies: ['Great Northeast D3', 'Great Northwest D2'] },
  BSC: { by: 'division', bodies: ['Big South', 'Big Sky'] },
  AEC: { by: 'division', bodies: ['Atlantic East D3', 'America East D1'] },
  ASC: { by: 'division', bodies: ['Atlantic Sun D1', 'American Southwest D3'] },
  AAA: { by: 'state', bodies: ['Arkansas Activities Association', 'a CLASSIFICATION, not a body'] },
}

/**
 * ⚠ CURATED COLLEGE-CONFERENCE ALIASES. Hand-written because no state
 * discriminates them, and three of these are RENAMES that no string rule could
 * derive:
 *
 *   American Athletic Conference -> "American Conference"      (2026 rebrand)
 *   Colonial Athletic Assn       -> "Coastal Athletic Association"
 *   ECFC                         -> "United East Conference"
 *
 * The first cost a false report: searching production for "American Athletic"
 * returned nothing and the D1 AAC was written up as absent from the universe when
 * it holds 23 records under its new name.
 */
export const SHEET_ALIASES = {
  'AAC|d1': 'American Conference',
  'AAC|naia': 'Appalachian Athletic Conference',
  'CAA|mid-major': 'Coastal Athletic Association',
  'ECFC|d3': 'United East Conference',
  'MEAC|mid-major': 'Mid-Eastern Athletic Conference',
  'SAC|d2': 'South Atlantic Conference',
  'SAC|naia': 'Sooner Athletic Conference',
  'MIAA|d2': 'Mid-America Intercollegiate Athletics Association',
  'MIAA|d3': 'Michigan Intercollegiate Athletic Association',
  'GNAC|d2': 'Great Northwest Athletic Conference',
  'MAC|d1': 'Mid-American Conference',
  'Middle Atlantic|d3': 'Middle Atlantic Conferences',
  'Big Sky|mid-major': 'Big Sky Conference',
  'Big South|mid-major': 'Big South Conference',
  'A-SUN|mid-major': 'Atlantic Sun Conference',
  'American SouthWest|d3': 'American Southwest Conference',
  'America East|mid-major': 'America East Conference',
}

/* ---- building the map ----------------------------------------------------- */

/**
 * Build the canonical body map from an observed census of raw `sr_conference`
 * values.
 *
 * @param census  Map|Array of { raw, count, states:Set|Array, segments:Set|Array }
 * @returns { merges, refusals, canonical, aliasIndex }
 *
 * `aliasIndex` maps a raw string to its canonical display name. A raw string with
 * no entry is its own body: absence from the index means "already canonical",
 * never "unknown".
 */
export function buildBodyMap(census) {
  const rows = normalizeCensus(census)
  const groups = new Map()
  for (const r of rows) {
    const key = looksLikeAcronym(r.raw)
      ? bareOf(r.raw).replace(/[^A-Za-z0-9]/g, '')
      : initialsOf(r.raw)
    if (!key || key.length < 3) continue
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(r)
  }

  const merges = []
  const refusals = []

  for (const [key, list] of groups) {
    if (list.length < 2) continue
    list.sort((a, b) => b.count - a.count)

    /* Partition by state. A member with no state goes to its own partition, so a
     * stateless acronym never silently joins a stated full name here. */
    const parts = new Map()
    for (const m of list) {
      const pk = m.state || ' none'
      if (!parts.has(pk)) parts.set(pk, [])
      parts.get(pk).push(m)
    }

    for (const [pk, members] of parts) {
      if (members.length < 2) continue
      const fulls = members.filter((m) => !m.acronym)
      const distinct = new Set(fulls.map((m) => bareOf(m.raw).toLowerCase()))
      if (distinct.size > 1) {
        refusals.push({ key, why: 'two or more distinct full names in one partition: different bodies sharing initials', members })
        continue
      }
      if (distinct.size === 1) {
        /* RULE 2. Every acronym in this partition must be state-unanimous. */
        const unsafe = members.filter((m) => m.acronym && m.states.length > 1)
        if (unsafe.length) {
          refusals.push({ key, why: `acronym spans ${unsafe[0].states.length} states: unanimity required, modal is not enough`, members })
          continue
        }
        merges.push({ key, state: pk === ' none' ? '' : pk, how: 'exact', members })
        continue
      }
      refusals.push({ key, why: 'all members are acronym-form: nothing to attach to', members })
    }

    /* A stateless acronym may still belong to a stated full name, but only when
     * the whole group offers exactly ONE candidate. Otherwise it is guessing.
     *
     * ⚠ AMBIGUOUS IS NOT THE SAME AS STATELESS, AND CONFLATING THEM REOPENED THE
     * BUG. An acronym whose records span two states normalizes to `state: ''`,
     * which made it look stateless and let it merge through this curated path
     * into the single full name available. That is the modal-state defect wearing
     * a different hat: bare "IHSAA" at 20 Idaho + 9 Indiana would have landed all
     * 29 on Idaho. A multi-state acronym is MORE ambiguous than a stateless one,
     * never less, so it is excluded here and refused below. */
    const multiState = list.filter((m) => m.acronym && m.states.length > 1)
    const stateless = list.filter((m) => m.acronym && !m.state && m.states.length === 0)
    const allFulls = list.filter((m) => !m.acronym)
    if (multiState.length) {
      refusals.push({
        key,
        why: `acronym spans ${multiState[0].states.length} states (${multiState[0].states.join(', ')}): unanimity required, modal is not enough`,
        members: [...multiState, ...allFulls],
      })
    }
    const distinctAll = new Set(allFulls.map((m) => bareOf(m.raw).toLowerCase()))
    if (stateless.length && distinctAll.size === 1) {
      const target = allFulls[0]
      const already = merges.some((g) => g.members.includes(target) && g.members.some((m) => stateless.includes(m)))
      if (!already) merges.push({ key, state: target.state, how: 'curated', members: [target, ...stateless] })
    } else if (stateless.length && distinctAll.size > 1) {
      refusals.push({ key, why: `stateless acronym with ${distinctAll.size} candidate full names: unguardable, refused`, members: [...stateless, ...allFulls] })
    }
  }

  /* Dedupe identical merge groups produced by both passes. */
  const seen = new Set()
  const clean = []
  for (const g of merges) {
    const sig = g.members.map((m) => m.raw).sort().join(' ')
    if (seen.has(sig)) continue
    seen.add(sig)
    clean.push(g)
  }

  const aliasIndex = new Map()
  for (const g of clean) {
    const canonical = g.members.find((m) => !m.acronym)?.raw || g.members[0].raw
    for (const m of g.members) if (m.raw !== canonical) aliasIndex.set(m.raw, canonical)
  }

  const absorbed = clean.reduce((s, g) => s + g.members.length - 1, 0)
  return {
    merges: clean,
    refusals,
    aliasIndex,
    rawCount: rows.length,
    canonical: rows.length - absorbed,
    absorbed,
    recordsAffected: clean.reduce((s, g) => s + g.members.reduce((t, m) => t + m.count, 0), 0),
  }
}

function normalizeCensus(census) {
  const entries = census instanceof Map ? [...census.entries()].map(([raw, v]) => ({ raw, ...v })) : [...(census || [])]
  return entries.map((e) => {
    const states = [...new Set((e.states instanceof Set ? [...e.states] : e.states || []).map(S).filter(Boolean))]
    const segments = [...new Set((e.segments instanceof Set ? [...e.segments] : e.segments || []).map(S).filter(Boolean))]
    return {
      raw: S(e.raw),
      count: Number(e.count) || 0,
      states,
      segments,
      /* The MODAL state is used for grouping only. Rule 2 tests UNANIMITY
       * separately against `states`, so a modal value can never decide a merge. */
      state: states.length === 1 ? states[0] : (e.modalState ? S(e.modalState) : ''),
      acronym: looksLikeAcronym(e.raw),
    }
  }).filter((r) => r.raw)
}

/** Resolve one raw value to its canonical body name. Absence means canonical. */
export function canonicalBody(raw, aliasIndex) {
  const s = S(raw)
  if (!s) return ''
  return aliasIndex?.get(s) || s
}

/**
 * Resolve a sheet CELL (written in acronyms) to a production body name.
 * Returns null and names the candidates rather than guessing.
 */
export function resolveSheetCell(token, jurisdiction, knownBodies) {
  const t = S(token).replace(/\*/g, '').replace(/\s*\([^)]*\)\s*$/, '').trim()
  const alias = SHEET_ALIASES[`${t}|${jurisdiction}`]
  if (alias) return { body: alias, how: 'curated alias', why: '' }
  if (knownBodies?.has?.(t)) return { body: t, how: 'exact', why: '' }
  const collision = KNOWN_COLLISIONS[t.toUpperCase()]
  if (collision) {
    return { body: null, how: 'refused', why: `"${t}" is ambiguous by ${collision.by}: ${collision.bodies.join(' | ')}. Needs a curated alias for jurisdiction "${jurisdiction}".` }
  }
  return { body: null, how: 'refused', why: `no curated match for "${t}" in jurisdiction "${jurisdiction}"` }
}
